import { useState, useEffect } from "react";
import { AppUser, Vendor, POStatus, POClassification } from "@po-management/types";
import { useApiClient } from "../auth/useApiClient";
import { useAdminPOs, AdminPOFilters } from "./useAdminPOs";

function useAdminUsers(): AppUser[] {
  const { get } = useApiClient();
  const [users, setUsers] = useState<AppUser[]>([]);
  useEffect(() => {
    get("/api/admin/users")
      .then((r) => r.json() as Promise<{ success: boolean; data: AppUser[] }>)
      .then((b) => { if (b.success) setUsers(b.data); })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return users;
}

function useApprovers(): { oid: string; name: string }[] {
  const { get } = useApiClient();
  const [approvers, setApprovers] = useState<{ oid: string; name: string }[]>([]);
  useEffect(() => {
    get("/api/admin/pos/approvers")
      .then((r) => r.json() as Promise<{ success: boolean; data: { oid: string; name: string }[] }>)
      .then((b) => { if (b.success) setApprovers(b.data); })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return approvers;
}

function useAllVendors(): Vendor[] {
  const { get } = useApiClient();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  useEffect(() => {
    get("/api/vendors?all=true")
      .then((r) => r.json() as Promise<{ success: boolean; data: Vendor[] }>)
      .then((b) => { if (b.success) setVendors(b.data); })
      .catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return vendors;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const th: React.CSSProperties = { padding: "8px 12px", textAlign: "left", fontSize: 12, color: "#666", fontWeight: 600, whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid #f0f0f0" };

// ── POs Tab ───────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: (POStatus | "")[] = ["", "Draft", "PendingApproval", "Approved", "Sent", "Received", "Rejected", "Void"];
const CLASS_OPTIONS: (POClassification | "")[] = ["", "None", "ESPA", "Symfonhtiko"];

function AdminPOsTab({ onViewPO }: { onViewPO: (id: string) => void }): JSX.Element {
  const [filters, setFilters] = useState<AdminPOFilters>({});
  const [draft, setDraft] = useState<AdminPOFilters>({});
  const { items, total, page, pageSize, loading, error, setPage } = useAdminPOs(filters);
  const approvers = useApprovers();
  const users = useAdminUsers();
  const vendors = useAllVendors();

  function applyFilters(): void { setFilters({ ...draft }); }
  function clearFilters(): void { setDraft({}); setFilters({}); }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20, padding: 16, background: "#f8f9fc", borderRadius: 6, border: "1px solid #eee" }}>
        <select value={draft.status ?? ""} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s || "All Statuses"}</option>)}
        </select>
        <select value={draft.creatorOid ?? ""} onChange={(e) => setDraft((d) => ({ ...d, creatorOid: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, maxWidth: 200 }}>
          <option value="">All Users</option>
          {users.map((u) => <option key={u.oid} value={u.oid}>{u.displayName}</option>)}
        </select>
        <select value={draft.assignedApproverOid ?? ""} onChange={(e) => setDraft((d) => ({ ...d, assignedApproverOid: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, maxWidth: 200 }}>
          <option value="">All Assignees</option>
          {approvers.map((a) => (
            <option key={a.oid} value={a.oid}>{a.name}</option>
          ))}
        </select>
        <select value={draft.vendorId ?? ""} onChange={(e) => setDraft((d) => ({ ...d, vendorId: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, maxWidth: 200 }}>
          <option value="">All Vendors</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}{!v.isActive ? " (inactive)" : ""}</option>)}
        </select>
        <select value={draft.classification ?? ""} onChange={(e) => setDraft((d) => ({ ...d, classification: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }}>
          {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c || "All Classifications"}</option>)}
        </select>
        <input type="date" value={draft.dateFrom ?? ""} onChange={(e) => setDraft((d) => ({ ...d, dateFrom: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
        <input type="date" value={draft.dateTo ?? ""} onChange={(e) => setDraft((d) => ({ ...d, dateTo: e.target.value || undefined }))}
          style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13 }} />
        <button onClick={applyFilters} style={{ padding: "6px 16px", background: "#5B6A8A", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13 }}>
          Apply
        </button>
        <button onClick={clearFilters} style={{ padding: "6px 12px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontSize: 13 }}>
          Clear
        </button>
      </div>

      {error && <div style={{ padding: "10px 14px", background: "#fff0f0", border: "1px solid #fcc", borderRadius: 4, color: "#c00", marginBottom: 12, fontSize: 13 }}>{error}</div>}

      <div style={{ fontSize: 13, color: "#888", marginBottom: 8 }}>
        {loading ? "Loading…" : `${total} PO${total !== 1 ? "s" : ""} found`}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
          <thead>
            <tr style={{ background: "#f5f6fa" }}>
              <th style={th}>PO Number</th>
              <th style={th}>Vendor</th>
              <th style={th}>Creator</th>
              <th style={th}>Status</th>
              <th style={th}>Classification</th>
              <th style={th}>Total Value</th>
              <th style={th}>Approver</th>
              <th style={th}>Document Date</th>
            </tr>
          </thead>
          <tbody>
            {items.map((po) => (
              <tr key={po.id} onClick={() => onViewPO(po.id)}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f6fa")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                <td style={{ ...td, color: "#5B6A8A", fontWeight: 600 }}>{po.poNumber}</td>
                <td style={td}>{po.vendorNameSnapshot}</td>
                <td style={td}>{po.createdByName}</td>
                <td style={td}>
                  <span style={{ padding: "2px 8px", borderRadius: 10, background: "#5B6A8A20", color: "#5B6A8A", fontSize: 12, fontWeight: 600 }}>
                    {po.status}
                  </span>
                </td>
                <td style={td}>{po.classification !== "None" ? po.classification : "—"}</td>
                <td style={{ ...td, textAlign: "right" }}>€ {Number(po.totalValue).toFixed(2)}</td>
                <td style={td}>{po.assignedApproverName ?? "—"}</td>
                <td style={td}>{String(po.documentDate).slice(0, 10)}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#888", padding: "24px" }}>No purchase orders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, fontSize: 13 }}>
          <button onClick={() => setPage(page - 1)} disabled={page <= 1}
            style={{ padding: "5px 12px", border: "1px solid #ccc", borderRadius: 4, cursor: page <= 1 ? "default" : "pointer", background: "#fff" }}>
            ← Previous
          </button>
          <span style={{ color: "#666" }}>Page {page} of {totalPages} ({total} total)</span>
          <button onClick={() => setPage(page + 1)} disabled={page >= totalPages}
            style={{ padding: "5px 12px", border: "1px solid #ccc", borderRadius: 4, cursor: page >= totalPages ? "default" : "pointer", background: "#fff" }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────

function AdminUsersTab(): JSX.Element {
  const { get, post } = useApiClient();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reassignSource, setReassignSource] = useState<AppUser | null>(null);
  const [targetOid, setTargetOid] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [reassignResult, setReassignResult] = useState<string | null>(null);

  useEffect(() => {
    get("/api/admin/users")
      .then((r) => r.json() as Promise<{ success: boolean; data: AppUser[] }>)
      .then((b) => { if (b.success) setUsers(b.data); else setError("Failed to load users"); })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleReassign(): Promise<void> {
    if (!reassignSource || !targetOid) return;
    const target = users.find((u) => u.oid === targetOid);
    if (!target) return;
    setReassigning(true);
    setReassignResult(null);
    try {
      const res = await post(`/api/admin/users/${reassignSource.oid}/reassign`, { targetOid, targetName: target.displayName });
      const body = await res.json() as { success: boolean; data?: { reassigned: number }; error?: string };
      if (body.success) {
        setReassignResult(`✓ ${body.data!.reassigned} PO${body.data!.reassigned !== 1 ? "s" : ""} reassigned to ${target.displayName}.`);
        setReassignSource(null);
        setTargetOid("");
      } else {
        setReassignResult(`Error: ${body.error ?? "Failed"}`);
      }
    } catch {
      setReassignResult("Network error");
    } finally {
      setReassigning(false);
    }
  }

  const spObjectId = import.meta.env.VITE_AZURE_SP_OBJECT_ID as string;
  const clientId = import.meta.env.VITE_AZURE_CLIENT_ID as string;
  const azurePortalUrl = `https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Users/objectId/${spObjectId}/appId/${clientId}/preferredSingleSignOnMode~/null/servicePrincipalType/Application/fromNav/`;

  if (loading) return <p style={{ fontSize: 13, color: "#888" }}>Loading users…</p>;
  if (error) return <div style={{ color: "#c00", fontSize: 13 }}>{error}</div>;

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
        To assign or change roles, use the{" "}
        <a href={azurePortalUrl} target="_blank" rel="noreferrer" style={{ color: "#5B6A8A" }}>
          Azure Portal App Registration
        </a>.
      </div>

      {reassignResult && (
        <div style={{ padding: "10px 14px", background: reassignResult.startsWith("✓") ? "#f0faf4" : "#fff0f0", border: `1px solid ${reassignResult.startsWith("✓") ? "#a7d7b8" : "#fcc"}`, borderRadius: 4, marginBottom: 16, fontSize: 13 }}>
          {reassignResult}
        </div>
      )}

      {reassignSource && (
        <div style={{ padding: 16, background: "#f0f4ff", border: "1px solid #b0c0e0", borderRadius: 6, marginBottom: 20 }}>
          <p style={{ margin: "0 0 10px", fontSize: 13 }}>
            Reassign all POs from <strong>{reassignSource.displayName}</strong> to:
          </p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <select value={targetOid} onChange={(e) => setTargetOid(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, minWidth: 220 }}>
              <option value="">— Select target user —</option>
              {users.filter((u) => u.oid !== reassignSource.oid).map((u) => (
                <option key={u.oid} value={u.oid}>{u.displayName} ({u.role})</option>
              ))}
            </select>
            <button onClick={() => void handleReassign()} disabled={!targetOid || reassigning}
              style={{ padding: "6px 16px", background: targetOid ? "#1B3A6B" : "#ccc", color: "#fff", border: "none", borderRadius: 4, cursor: !targetOid || reassigning ? "default" : "pointer", fontSize: 13 }}>
              {reassigning ? "Reassigning…" : "Confirm Reassign"}
            </button>
            <button onClick={() => { setReassignSource(null); setTargetOid(""); }}
              style={{ padding: "6px 12px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontSize: 13 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f5f6fa" }}>
            <th style={th}>Display Name</th>
            <th style={th}>Email</th>
            <th style={th}>Role</th>
            <th style={th}>Last Login</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.oid}>
              <td style={td}>{u.displayName}</td>
              <td style={td}>{u.email}</td>
              <td style={td}><span style={{ padding: "2px 8px", borderRadius: 10, background: "#5B6A8A20", color: "#5B6A8A", fontSize: 12, fontWeight: 600 }}>{u.role}</span></td>
              <td style={{ ...td, color: "#888" }}>{new Date(u.lastLoginAt).toLocaleString()}</td>
              <td style={td}>
                <button onClick={() => { setReassignSource(u); setTargetOid(""); setReassignResult(null); }}
                  style={{ padding: "4px 10px", background: "#fff", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
                  Reassign POs
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Vendors Tab ───────────────────────────────────────────────────────────────

function AdminVendorsTab(): JSX.Element {
  const { get, put, patch } = useApiClient();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Vendor>>({});
  const [saving, setSaving] = useState(false);

  function loadVendors(): void {
    get("/api/vendors?all=true")
      .then((r) => r.json() as Promise<{ success: boolean; data: Vendor[] }>)
      .then((b) => { if (b.success) setVendors(b.data); else setError("Failed to load vendors"); })
      .catch(() => setError("Network error"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadVendors();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startEdit(v: Vendor): void {
    setEditingId(v.id);
    setEditForm({ name: v.name, address: v.address, vat: v.vat, taxOffice: v.taxOffice, defaultPaymentTerms: v.defaultPaymentTerms });
  }

  async function saveEdit(id: string): Promise<void> {
    setSaving(true);
    try {
      const res = await put(`/api/vendors/${id}`, editForm);
      const body = await res.json() as { success: boolean; data?: Vendor; error?: string };
      if (body.success) {
        setVendors((vs) => vs.map((v) => v.id === id ? { ...v, ...body.data } : v));
        setEditingId(null);
      } else {
        setError(body.error ?? "Failed to save");
      }
    } catch { setError("Network error"); }
    finally { setSaving(false); }
  }

  async function toggleActive(v: Vendor): Promise<void> {
    const action = v.isActive ? "deactivate" : "reactivate";
    const res = await patch(`/api/vendors/${v.id}/${action}`, {});
    const body = await res.json() as { success: boolean; data?: Vendor; error?: string };
    if (body.success) {
      setVendors((vs) => vs.map((x) => x.id === v.id ? { ...x, isActive: !v.isActive } : x));
    } else {
      setError(body.error ?? `Failed to ${action}`);
    }
  }

  const filtered = vendors.filter((v) =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.vat.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <p style={{ fontSize: 13, color: "#888" }}>Loading vendors…</p>;
  if (error) return <div style={{ color: "#c00", fontSize: 13 }}>{error}</div>;

  return (
    <div>
      <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or VAT…"
        style={{ padding: "7px 12px", border: "1px solid #ccc", borderRadius: 4, fontSize: 13, width: 280, marginBottom: 16 }} />

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#f5f6fa" }}>
            <th style={th}>Vendor Name</th>
            <th style={th}>VAT</th>
            <th style={th}>Tax Office</th>
            <th style={th}>Payment Terms</th>
            <th style={th}>Status</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((v) => (
            editingId === v.id ? (
              <tr key={v.id} style={{ background: "#f8f9fc" }}>
                <td style={td}><input value={editForm.name ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13, boxSizing: "border-box" }} /></td>
                <td style={td}><input value={editForm.vat ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, vat: e.target.value }))} style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13, boxSizing: "border-box" }} /></td>
                <td style={td}><input value={editForm.taxOffice ?? ""} onChange={(e) => setEditForm((f) => ({ ...f, taxOffice: e.target.value }))} style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13, boxSizing: "border-box" }} /></td>
                <td style={td}><input type="number" value={editForm.defaultPaymentTerms ?? 60} onChange={(e) => setEditForm((f) => ({ ...f, defaultPaymentTerms: parseInt(e.target.value, 10) }))} style={{ width: 70, padding: "4px 8px", border: "1px solid #ccc", borderRadius: 3, fontSize: 13 }} /></td>
                <td style={td}><span style={{ color: v.isActive ? "#059669" : "#888", fontWeight: 600, fontSize: 12 }}>{v.isActive ? "Active" : "Deactivated"}</span></td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => void saveEdit(v.id)} disabled={saving} style={{ padding: "4px 10px", background: "#059669", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Save</button>
                    <button onClick={() => setEditingId(null)} style={{ padding: "4px 10px", background: "#fff", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Cancel</button>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={v.id} style={{ opacity: v.isActive ? 1 : 0.6 }}>
                <td style={td}>{v.name}</td>
                <td style={td}>{v.vat}</td>
                <td style={td}>{v.taxOffice}</td>
                <td style={td}>{v.defaultPaymentTerms} days</td>
                <td style={td}><span style={{ color: v.isActive ? "#059669" : "#888", fontWeight: 600, fontSize: 12 }}>{v.isActive ? "Active" : "Deactivated"}</span></td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => startEdit(v)} style={{ padding: "4px 10px", background: "#fff", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Edit</button>
                    <button onClick={() => void toggleActive(v)}
                      style={{ padding: "4px 10px", background: v.isActive ? "#fff" : "#059669", color: v.isActive ? "#dc2626" : "#fff", border: `1px solid ${v.isActive ? "#dc2626" : "#059669"}`, borderRadius: 3, cursor: "pointer", fontSize: 12 }}>
                      {v.isActive ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                </td>
              </tr>
            )
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "#888", padding: "24px" }}>No vendors found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Admin Panel Shell ─────────────────────────────────────────────────────────

type AdminTab = "pos" | "users" | "vendors";

interface AdminPanelProps {
  onDone: () => void;
  onViewPO: (id: string) => void;
}

export function AdminPanel({ onDone, onViewPO }: AdminPanelProps): JSX.Element {
  const [tab, setTab] = useState<AdminTab>("pos");

  return (
    <div style={{ padding: "2rem", fontFamily: "sans-serif", maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <button onClick={onDone} style={{ background: "none", border: "none", color: "#5B6A8A", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 0 }}>←</button>
        <h2 style={{ margin: 0, color: "#5B6A8A" }}>Admin Panel</h2>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "2px solid #eee" }}>
        {(["pos", "users", "vendors"] as AdminTab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: "8px 24px", background: "none", border: "none",
                     borderBottom: tab === t ? "2px solid #5B6A8A" : "2px solid transparent",
                     color: tab === t ? "#5B6A8A" : "#888", fontWeight: tab === t ? 600 : 400,
                     cursor: "pointer", fontSize: 14, marginBottom: -2 }}>
            {t === "pos" ? "Purchase Orders" : t === "users" ? "Users" : "Vendors"}
          </button>
        ))}
      </div>

      {tab === "pos"     && <AdminPOsTab onViewPO={onViewPO} />}
      {tab === "users"   && <AdminUsersTab />}
      {tab === "vendors" && <AdminVendorsTab />}
    </div>
  );
}
